import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Spacer } from '@tink/core/src/components/atoms/Spacer/Spacer';
import { MarketCode } from '@tink/config/src/markets';
import { LoadingSpinner } from '../../../../../atoms/LoadingSpinner/LoadingSpinner';
import { ContentBody } from '../../../../../atoms/ContentBody/ContentBody';
import { Footer } from '../../../../../atoms/Footer/Footer';
import { DataAccessProviders, Provider, ProviderEntity } from '../../../../../../models/provider';
import { Header as HeaderImport, HeaderProps } from '../../../../../organisms/Header/Header';
import { ScreenWrapper } from '../../../../../atoms/ScreenWrapper/ScreenWrapper';
import { PageView } from '../../../../../../models/analytics';
import { ProviderInputsWrapper } from '../../../../SubmitCredentials/molecules/ProviderInputs/ProviderInputsWrapper';
import {
  HeaderContainer,
  StyledError,
  StyledUserHintId,
} from '../../SubmitCredentialsScreen.styles';
import { ApplicationUnverifiedLabel } from '../../../../../molecules/ApplicationUnverifiedLabel/ApplicationUnverifiedLabel';
import { UserFeedbackButton as UserFeedbackButtonImport } from '../../../../ExitFeedback/UserFeedbackButton';
import { messages } from '../../../../SubmitCredentials/messages';
import { LegalDisclaimer as LegalDisclaimerImport } from '../../../../../molecules/LegalDisclaimer/LegalDisclaimer';
import { SubmitProviderInputsPayload } from '../../../../SubmitCredentials/molecules/ProviderInputs/ProviderInputs';
import { BackButton } from '../../../../../organisms/Header/HeaderMolecule';
import { usePageViewEvents } from '../../../../../../core/Analytics/usePageViewEvents';
import { PrimaryButton } from '../../../../../atoms/Button/Button';
import { useUrlQuery } from '../../../../../ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { BANK_LOGO_PLACEHOLDER } from '../../../../../../config';
import { ProviderTitleHeader } from './ProviderTitleHeader';
import { LoginTitleHeader } from './LoginTitleHeader';
import { LoginTitleScopeModal } from './LoginTitleScopeModal';

export type SubmitCredentialsScreenViewProps = {
  bodyBlock?: JSX.Element;
  providerFieldsFallback?: JSX.Element;
  userHintId?: string;
  buttonLabel?: JSX.Element;
  provider: ProviderEntity;
  renderProviderFields: boolean;
  onSubmitCredentials: (payload: SubmitProviderInputsPayload) => void;
  legalDisclaimer?: JSX.Element;
  isCredentialsLoading: boolean;
  Header?: React.ComponentType<HeaderProps>;
  credentialsError?: string;
  isClientVerified: boolean;
  username?: string;
  personalIdentifier?: string;
  providerFieldsVisible: boolean;
  shouldShowUserHintId: boolean;
  isUserFeedbackEnabled: boolean;
  UserFeedbackButton?: React.ComponentType;
  displaySpinnerInButton?: boolean;
} & BackButton;

export const SubmitCredentialsScreenView: React.FC<SubmitCredentialsScreenViewProps> = ({
  userHintId,
  provider,
  credentialsError,
  isClientVerified,
  renderProviderFields,
  onSubmitCredentials,
  onBack,
  buttonLabel,
  bodyBlock,
  providerFieldsFallback,
  legalDisclaimer = LegalDisclaimerImport,
  isCredentialsLoading,
  Header = HeaderImport,
  username,
  personalIdentifier,
  providerFieldsVisible,
  shouldShowUserHintId,
  isUserFeedbackEnabled,
  UserFeedbackButton = UserFeedbackButtonImport,
  displaySpinnerInButton = false,
}) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const pageView = PageView.SUBMIT_CREDENTIALS_SCREEN;

  usePageViewEvents({
    pageView,
  });

  const { formatMessage } = useIntl();

  const { market } = useUrlQuery();
  const isMarketUS = market === MarketCode.US;

  let passwordHelpText = provider.passwordHelpText || formatMessage(messages.loginCredentialsTip);
  let providerIcon = Provider.getIcon(provider);

  if (isMarketUS) {
    passwordHelpText = formatMessage(messages.loginCredentialsDataAccessProvider, {
      dataAccessProvider: DataAccessProviders.MX,
    });
    providerIcon = BANK_LOGO_PLACEHOLDER;
  }

  return (
    <ScreenWrapper data-testid="addCredentialsWrapper">
      <Header onBack={onBack} pageView={pageView} addBottomMargin />
      <ContentBody>
        {providerFieldsVisible && (
          <>
            {isMarketUS ? (
              <LoginTitleScopeModal passwordHelpText={passwordHelpText} />
            ) : (
              <LoginTitleHeader passwordHelpText={passwordHelpText} />
            )}
            <HeaderContainer>
              <ProviderTitleHeader providerName={provider.displayName} iconSrc={providerIcon} />
              {userHintId && (
                <StyledUserHintId>{shouldShowUserHintId ? userHintId : undefined}</StyledUserHintId>
              )}
            </HeaderContainer>
          </>
        )}

        <ProviderInputsWrapper
          setButtonDisabled={setIsButtonDisabled}
          providerFields={provider.fields}
          fixedValue={personalIdentifier || username || undefined}
          onSubmit={onSubmitCredentials}
          renderProviderFields={renderProviderFields}
          isSplitFields={Provider.isSplitFieldsProvider(provider)}
        >
          {credentialsError && (
            <>
              <StyledError data-testid="credentials-error">{credentialsError}</StyledError>
              <Spacer size="small" />
            </>
          )}
          <ApplicationUnverifiedLabel isVerified={isClientVerified} />
          {bodyBlock}
          {!providerFieldsVisible && providerFieldsFallback}
          <Footer stickToBottom>
            {!displaySpinnerInButton &&
              (isCredentialsLoading ? (
                <LoadingSpinner />
              ) : (
                <PrimaryButton
                  disabled={isButtonDisabled}
                  type="submit"
                  color="primary"
                  variant="contained"
                  data-testid="submit-credentials-button"
                  data-test="submitCredentialsButton"
                  pageView={pageView}
                >
                  {buttonLabel || <FormattedMessage {...messages.continue} />}
                </PrimaryButton>
              ))}

            {displaySpinnerInButton && (
              <PrimaryButton
                disabled={isButtonDisabled}
                loadingSpinner={isCredentialsLoading}
                type="submit"
                color="primary"
                variant="contained"
                data-testid="submit-credentials-button"
                data-test="submitCredentialsButton"
                pageView={pageView}
              >
                {buttonLabel || <FormattedMessage {...messages.continue} />}
              </PrimaryButton>
            )}

            {isUserFeedbackEnabled && <UserFeedbackButton />}
          </Footer>
        </ProviderInputsWrapper>
      </ContentBody>
      {legalDisclaimer}
    </ScreenWrapper>
  );
};
